import ReconnectingWebSocket from 'reconnecting-websocket';

import { logEvent, logException } from '#/features/logging/logging';
import { userUnauthorizedTopic } from '#/features/auth/auth-topics';
import { getAuthToken } from '#/api/auth';

import createTopic, { EventHandler } from '#/utils/createTopic';

export interface ApiWebSocket<N = WSNotification> {
  readonly request: (reqParams: WSReqParams) => Promise<WSResponse<any>>; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** @returns Unsubscribe function */
  readonly addNotificationHandler: (handler: EventHandler<N>) => () => void;
  readonly removeNotificationHandler: (handler: EventHandler<N>) => void;
  readonly readyState: ReconnectingWebSocket['readyState'];
  readonly close: ReconnectingWebSocket['close'];
  readonly reconnect: ReconnectingWebSocket['reconnect'];
  readonly addEventListener: ReconnectingWebSocket['addEventListener'];
  readonly removeEventListener: ReconnectingWebSocket['removeEventListener'];
}

export interface WSRequest {
  readonly jsonrpc: '2.0';
  readonly id: string | number;
  readonly method: string;
  readonly params?: { readonly [key: string]: unknown };
}

export interface WSNotification {
  readonly jsonrpc: '2.0';
  readonly method: string;
  readonly params: {
    readonly channel: string;
    readonly data: unknown;
  };
}

interface RawWSResponse<T = unknown> {
  readonly jsonrpc: '2.0';
  readonly id: string | number;
  readonly result?: T;
  readonly error?: WSError;
}

export interface WSResponse<T = unknown>
  extends Omit<RawWSResponse<T>, 'error'> {
  readonly error?: Error;
}

export interface WSError {
  readonly code?: number;
  readonly message?: string;
  readonly data?: {
    readonly method?: string;
    readonly timestamp?: number;
  };
}

export type AsyncWSResp<T> = Promise<WSResponse<T>>;

export interface WSReqParams {
  readonly method: string;
  readonly params?: Record<string, unknown>;
  readonly signal?: AbortSignal;
  /** Milliseconds, defaults to 5000 ms. Use <= 0 to disable. */
  readonly timeout?: number;
}

type ReqCallback = (resp: RawWSResponse<unknown>) => void;

export interface CreateWebSocketOptions<
  RawNotification extends WSNotification,
  Notification,
> {
  readonly urlProvider: () => Promise<string>;
  readonly processNotification: (raw: RawNotification) => Notification;
}

/**
 * Create an instance of our lightweight abstraction over a WebSocket.
 *
 * Key features are:
 * - Auto reconnection when the connection closes
 * - Auto reconnection when the connection fails to open
 * - Auto reconnection when a request times out (no response received)
 * - JSON-RPC requests
 * - Notification messages
 */
export default function createWebSocket<
  RawNotification extends WSNotification = WSNotification,
  Notification = WSNotification,
>(
  options: CreateWebSocketOptions<RawNotification, Notification>,
): ApiWebSocket<Notification> {
  const { urlProvider, processNotification } = options;

  const ws =
    IS_RUNNING_IN_CYPRESS === 'true'
      ? new ReconnectingWebSocket(API_URL_WS, [], {
          WebSocket: window.MockedWebSocket,
        })
      : new ReconnectingWebSocket(urlProvider);

  const notificationTopic = createTopic<Notification>('ws-notification');
  const getId = createCounter();
  const reqCallbacksMap = new Map<string | number, ReqCallback>();

  async function request(reqParams: WSReqParams) {
    const { signal, timeout = 5000, ...reqRest } = reqParams;
    const id = getId();

    const req: WSRequest = { id, jsonrpc: '2.0', ...reqRest };

    ws.send(JSON.stringify(req));

    return new Promise<WSResponse>((_resolve, _reject) => {
      const resolve = (resp: RawWSResponse) => {
        cleanUp();

        if (resp.error == null) {
          _resolve({ ...resp, error: undefined });
          return;
        }

        const error = new Error(
          `WebSocket request \`${req.method}\` failed with ` +
            `code ${resp.error.code}: ${resp.error.message}`,
        );
        _resolve({ ...resp, error });
      };

      const reject = (error: Error) => {
        cleanUp();
        _reject(error);
      };

      reqCallbacksMap.set(id, resolve);

      const timeoutHandle = setTimeout(() => {
        reject(new TimeoutError('Web socket request timed out'));
        // Force reconnetion to prevent the connection from getting stuck
        if (ws.readyState === WebSocket.OPEN) {
          logEvent('WebSocket request timed out, forcing reconnection');
          ws.reconnect();
        }
      }, timeout);

      const handleAbort = () =>
        reject(new AbortError('Web socket request aborted'));
      signal?.addEventListener('abort', handleAbort);

      const cleanUp = () => {
        reqCallbacksMap.delete(id);
        clearTimeout(timeoutHandle);
        signal?.removeEventListener('abort', handleAbort);
      };
    });
  }

  function authHandler(parsedMsg: RawWSResponse): void {
    const isUnauthorized = parsedMsg.error?.code === 40111;

    if (isUnauthorized) {
      userUnauthorizedTopic.publish({});
    }
  }

  function responseHandler(parsedMsg: RawWSResponse): void {
    reqCallbacksMap.get(parsedMsg.id)?.(parsedMsg);
  }

  function subscriptionHandler(parsedMsg: RawNotification): void {
    notificationTopic.publish(processNotification(parsedMsg));
  }

  function wsMessageListener(message: MessageEvent<string>): void {
    const parsedMsg = JSON.parse(message.data) as
      | RawWSResponse
      | RawNotification;
    const isResponse = 'id' in parsedMsg;

    if (isResponse) {
      // message due to a request (response)
      authHandler(parsedMsg);
      responseHandler(parsedMsg);
    } else {
      // message due to a subscription (notification)
      subscriptionHandler(parsedMsg);
    }
  }

  ws.addEventListener('message', wsMessageListener);

  const apiWs: ApiWebSocket<Notification> = {
    request,
    addNotificationHandler: notificationTopic.subscribe,
    removeNotificationHandler: notificationTopic.unsubscribe,
    get readyState() {
      return ws.readyState;
    },
    close: ws.close.bind(ws),
    reconnect: ws.reconnect.bind(ws),
    addEventListener: ws.addEventListener.bind(ws),
    removeEventListener: ws.removeEventListener.bind(ws),
  } as const;

  enhanceWithAuthOnReconnect(apiWs);

  return apiWs;
}

function enhanceWithAuthOnReconnect<N>(ws: ApiWebSocket<N>) {
  function authenticateOnReconnect() {
    if (getAuthToken().length === 0) return;

    ws.request({
      method: 'auth',
      params: { bearer: getAuthToken() },
      timeout: 10_000,
    }).catch((err) => {
      const message = 'Failed to authenticate WS connection on reconnect';
      logException(new Error(message, { cause: err }));
    });
  }

  ws.addEventListener('open', authenticateOnReconnect);
}

class AbortError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AbortError';
  }
}

class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export function createCounter() {
  const MAX_I32 = 2147483647;
  let value = 0;

  return () => {
    if (value === MAX_I32) {
      return (value = 0);
    }
    return value++;
  };
}
